import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, MAILTO } from "../lib/brand";

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const navItems = [
        { label: "Home", to: "/" },
        { label: "How It Works", to: "/how-it-works" },
        { label: "Contact", to: "/#contact", hash: true },
    ];

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/80 backdrop-blur-md border-b border-black/5"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <nav className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-3"
                    aria-label="FreeByte home"
                >
                    <img
                        src={LOGO_URL}
                        alt="FreeByte"
                        className="h-10 md:h-12 w-auto object-contain"
                        data-testid="nav-logo-image"
                    />
                    <span
                        data-testid="nav-logo-slogan"
                        className="hidden sm:inline-block text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-neutral-500 leading-tight border-l border-black/10 pl-3"
                    >
                        The Rewarded
                        <br />
                        Growth Engine
                    </span>
                </Link>

                <ul className="hidden md:flex items-center gap-10">
                    {navItems.map((item) => (
                        <li key={item.label}>
                            {item.hash ? (
                                <a
                                    href={item.to}
                                    data-testid={`nav-link-${item.label
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                    className="link-underline text-[13px] tracking-wide text-neutral-700 hover:text-black transition-colors"
                                >
                                    {item.label}
                                </a>
                            ) : (
                                <Link
                                    to={item.to}
                                    data-testid={`nav-link-${item.label
                                        .toLowerCase()
                                        .replace(/\s+/g, "-")}`}
                                    className="link-underline text-[13px] tracking-wide text-neutral-700 hover:text-black transition-colors"
                                >
                                    {item.label}
                                </Link>
                            )}
                        </li>
                    ))}
                </ul>

                <a
                    href={MAILTO}
                    data-testid="nav-contact-cta"
                    className="hidden md:inline-flex items-center justify-center h-10 px-5 rounded-full bg-black text-white text-[13px] font-medium tracking-wide hover:bg-neutral-800 transition-colors"
                >
                    Contact Us
                </a>

                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    data-testid="nav-mobile-toggle"
                    className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-black/5 transition-colors"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </nav>

            {/* Mobile menu */}
            <div
                data-testid="nav-mobile-panel"
                className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
                    open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                } bg-white border-b border-black/5`}
            >
                <div className="px-6 py-6 flex flex-col gap-5">
                    {navItems.map((item) =>
                        item.hash ? (
                            <a
                                key={item.label}
                                href={item.to}
                                data-testid={`mobile-nav-${item.label
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                className="text-base text-neutral-800"
                            >
                                {item.label}
                            </a>
                        ) : (
                            <Link
                                key={item.label}
                                to={item.to}
                                data-testid={`mobile-nav-${item.label
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                className="text-base text-neutral-800"
                            >
                                {item.label}
                            </Link>
                        )
                    )}
                    <a
                        href={MAILTO}
                        data-testid="mobile-nav-contact-cta"
                        className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-black text-white text-sm font-medium tracking-wide"
                    >
                        Contact Us
                    </a>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
